import PropTypes from "prop-types";
import { PageTitle, Card, Btn, Spinner, Icon } from "../components";
import { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { useBreakpoints } from "../hooks/useMediaQuery";

const normalizeDateKey = (value) => {
  if (!value) return "";
  const normalized = String(value).substring(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : "";
};

const buildDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

import { useTenant } from "../context/TenantContext";
import { getDestinoLabel } from "../utils/tenantLabels";
import { getOperacionalStatusMeta } from "../utils/pedidoLabels";
import { groupOrdersIntoTrips } from "../utils/vanBoard";

const weekdayShort = (dateKey) => new Date(`${dateKey}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" }).replace(".", "");

// Viagens dos próximos dias ainda não enviadas: sem montagem (A fazer) ou em montagem.
const buildUpcomingTrips = (aFazer = [], emMontagem = [], todayKey) => {
  const upcoming = (items) => items.filter((item) => normalizeDateKey(item.data) >= todayKey);
  const notBuilt = groupOrdersIntoTrips(upcoming(aFazer)).map((trip) => ({
    key: `fazer-${trip.key}`, first: trip.first, pedidos: trip.items.length, passageiros: trip.passengers, built: false,
  }));
  const byRoute = new Map();
  for (const item of upcoming(emMontagem)) {
    const key = item.rota_operacional_id || item.id;
    if (!byRoute.has(key)) byRoute.set(key, []);
    byRoute.get(key).push(item);
  }
  const building = [...byRoute.entries()].map(([key, items]) => ({
    key: `montagem-${key}`, first: items[0], pedidos: items.length,
    passageiros: items.reduce((sum, item) => sum + (Number(item.total_passageiros) || 0), 0), built: true,
  }));
  return [...notBuilt, ...building]
    .sort((a, b) => `${normalizeDateKey(a.first.data)} ${a.first.horario}`.localeCompare(`${normalizeDateKey(b.first.data)} ${b.first.horario}`))
    .slice(0, 6);
};

const formatTrecho = (trecho, config) => {
  const destino = getDestinoLabel(config);
  return trecho === "ida" ? `Ida → ${destino}` : "Volta → Residências";
};

const getStatusMeta = (status) => {
  const meta = getOperacionalStatusMeta(status);
  return {
    family: meta.label,
    sub: null,
    bg: meta.bg,
    color: meta.color,
  };
};

function StatusBadge({ status }) {
  const m = getStatusMeta(status);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "2px 10px",
      borderRadius: 9999, fontSize: '0.8125rem', fontWeight: 600,
      background: m.bg, color: m.color,
    }}>
      {m.family}
      {m.sub && <span style={{ opacity: 0.65, fontWeight: 400 }}> · {m.sub}</span>}
    </span>
  );
}

function RouteRow({ route, onViewBacklog, isMobile }) {
  const { config } = useTenant();
  const regions = Array.isArray(route.regioes) ? route.regioes : [];
  const drivers = Array.isArray(route.motoristas) ? route.motoristas : [];

  return (
    <div
      onClick={onViewBacklog}
      role="button"
      tabIndex={0}
      aria-label={`Acompanhar rota ${route.id_pedido || route.id} às ${route.horario || "horário não informado"}`}
      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onViewBacklog(); } }}
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr auto",
        gap: isMobile ? 8 : 16,
        padding: "14px 16px",
        borderBottom: "1px solid var(--border)",
        cursor: "pointer",
        transition: "background 0.15s",
        alignItems: "center",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <strong style={{ fontSize: '1rem' }}>PG {route.id_pedido || `Rota ${route.id}`}</strong><StatusBadge status={route.status_operacional || route.status} />
          <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: "var(--text)" }}>
            {formatTrecho(route.trecho, config)}
          </span>
          {route.horario && (
            <span style={{ fontSize: '0.875rem', color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
              {route.horario}
            </span>
          )}
        </div>
        {regions.length > 0 && (
          <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
            {regions.slice(0, 4).join(" · ")}{regions.length > 4 ? ` +${regions.length - 4}` : ""}
          </div>
        )}
      </div>
      <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
        {drivers.length > 0
          ? drivers.slice(0, 2).join(", ") + (drivers.length > 2 ? ` +${drivers.length - 2}` : "")
          : <span style={{ fontStyle: "italic" }}>Sem motorista</span>}
      </div>
      {!isMobile && (
        <span style={{ fontSize: '0.875rem', color: "var(--accent)", fontWeight: 500 }}>Ver →</span>
      )}
    </div>
  );
}

RouteRow.propTypes = { route: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string,PropTypes.number]), id_pedido: PropTypes.string, horario: PropTypes.string, trecho: PropTypes.string, status: PropTypes.string, status_operacional: PropTypes.string, regioes: PropTypes.array, motoristas: PropTypes.array }).isRequired, onViewBacklog: PropTypes.func, isMobile: PropTypes.bool };

export default function Dashboard({ pessoas, motoristas, veiculos, setPage, openPedidos, userName }) {
  const { config } = useTenant();
  const { isMobile } = useBreakpoints();
  const [routes, setRoutes] = useState([]);
  const [oldDraftsCount, setOldDraftsCount] = useState(0);
  const [todayWithoutDriverCount, setTodayWithoutDriverCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [upcoming, setUpcoming] = useState({ loading: true, trips: [], error: "" });

  const loadUpcoming = () => {
    setUpcoming((previous) => ({ ...previous, loading: true, error: "" }));
    Promise.all([
      apiFetch("/programacoes?status=rascunho&limit=100"),
      apiFetch("/programacoes?rota_status=planejamento&limit=100"),
    ])
      .then(([aFazer, emMontagem]) => setUpcoming({
        loading: false, error: "",
        trips: buildUpcomingTrips(aFazer?.items || [], emMontagem?.items || [], buildDateKey(new Date())),
      }))
      .catch(() => setUpcoming({ loading: false, trips: [], error: "Não foi possível carregar as próximas viagens." }));
  };

  const loadSummary = () => {
    setLoading(true);
    setError("");
    apiFetch("/rotas?summary=1")
      .then((data) => {
        setOldDraftsCount(Number(data?.old_drafts_count) || 0);
        setTodayWithoutDriverCount(Number(data?.today_without_driver_count) || 0);
        setRoutes(Array.isArray(data?.today_routes) ? data.today_routes : []);
      })
      .catch(() => setError("Não foi possível carregar as rotas de hoje."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadSummary(); loadUpcoming(); }, []);

  const oldDrafts = oldDraftsCount > 0 ? Array(oldDraftsCount).fill(null) : [];
  const noDriverRoutes = todayWithoutDriverCount > 0 ? Array(todayWithoutDriverCount).fill(null) : [];

  const stats = [
    { label: "Funcionários", value: pessoas.length, action: "pessoas" },
    { label: "Motoristas",   value: motoristas.length, action: "motoristas" },
    { label: "Veículos",     value: veiculos.length, action: "veiculos" },
  ];


  const todayFormatted = new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="fade-in">
      <header className="rf-dashboard-header">
        <div><PageTitle icon="dashboard">Dashboard</PageTitle>
          <p className="rf-dashboard-date">Olá, {userName}. Hoje é {todayFormatted}.</p>
        </div>
        <div className="rf-dashboard-actions">
          <Btn variant="secondary" onClick={() => openPedidos?.({ tab: "operacao" })}>Em operação</Btn>
          <Btn onClick={() => openPedidos?.({ tab: "a_fazer" })}>Montar vans</Btn>
        </div>
      </header>

      {/* Alertas */}
      {(oldDrafts.length > 0 || noDriverRoutes.length > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {oldDrafts.length > 0 && (
            <button
              type="button"
              onClick={() => openPedidos?.({ tab: "operacao" })}
              style={{
                textAlign: "left", fontFamily: "inherit", minHeight: 44,
                display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                borderRadius: 8, background: "rgba(234,179,8,0.08)",
                border: "1px solid rgba(234,179,8,0.25)", cursor: "pointer",
              }}
            >
              <span style={{ fontSize: '0.9375rem', color: "#fde68a", fontWeight: 500 }}>
                {oldDrafts.length} pedido{oldDrafts.length > 1 ? "s" : ""} antigo{oldDrafts.length > 1 ? "s" : ""} aguardando envio
              </span>
              <span style={{ fontSize: '0.875rem', color: "var(--muted)", marginLeft: "auto" }}>Conferir →</span>
            </button>
          )}
          {noDriverRoutes.length > 0 && (
            <button
              type="button"
              onClick={() => openPedidos?.({ tab: "operacao" })}
              style={{
                textAlign: "left", fontFamily: "inherit", minHeight: 44,
                display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                borderRadius: 8, background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.25)", cursor: "pointer",
              }}
            >
              <span style={{ fontSize: '0.9375rem', color: "#fca5a5", fontWeight: 500 }}>
                {noDriverRoutes.length} rota{noDriverRoutes.length > 1 ? "s" : ""} de hoje sem motorista atribuído
              </span>
              <span style={{ fontSize: '0.875rem', color: "var(--muted)", marginLeft: "auto" }}>Conferir →</span>
            </button>
          )}
        </div>
      )}

      {/* Rotas de hoje */}
      <Card style={{ marginBottom: 20, padding: 0, overflow: "hidden" }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 16px", borderBottom: "1px solid var(--border)",
        }}>
          <h3 style={{ fontWeight: 600, fontSize: '1rem' }}>Rotas de hoje</h3>
          <Btn variant="secondary" size="sm" onClick={() => openPedidos?.({ tab: "operacao" })}>
            Ver todas
          </Btn>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <Spinner />
          </div>
        ) : error ? (
          <div role="alert" style={{ padding: "28px 16px", textAlign: "center" }}>
            <p style={{ color: "#fca5a5", marginBottom: 14 }}>{error}</p>
            <Btn onClick={loadSummary} size="sm">Tentar novamente</Btn>
          </div>
        ) : routes.length === 0 ? (
          <div style={{ padding: "28px 16px", textAlign: "center" }}>
            <p style={{ color: "var(--muted)", fontSize: '1rem' }}>
              Nenhuma van sai hoje. As próximas viagens aparecem abaixo.
            </p>
          </div>
        ) : (
          <div>
            {routes.map(route => (
              <RouteRow
                key={route.id}
                route={route}
                onViewBacklog={() => openPedidos?.({ tab: "operacao", routeId: route.id })}
                isMobile={isMobile}
              />
            ))}
          </div>
        )}
      </Card>

      <Card style={{ marginBottom: 20, padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ fontWeight: 600, fontSize: '1rem' }}>Próximas viagens</h3>
          <Btn variant="secondary" size="sm" onClick={() => setPage("documentos")}>Documentos</Btn>
        </div>
        {upcoming.loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><Spinner /></div>
        ) : upcoming.error ? (
          <div role="alert" style={{ padding: "28px 16px", textAlign: "center" }}>
            <p style={{ color: "#fca5a5", marginBottom: 14 }}>{upcoming.error}</p>
            <Btn onClick={loadUpcoming} size="sm">Tentar novamente</Btn>
          </div>
        ) : upcoming.trips.length === 0 ? (
          <p style={{ padding: "28px 16px", textAlign: "center", color: "var(--muted)" }}>
            Nenhuma viagem para montar. Cadastre ou importe pedidos para começar.
          </p>
        ) : (
          <ul className="rf-upcoming">
            {upcoming.trips.map((trip) => {
              const dateKey = normalizeDateKey(trip.first.data);
              return <li key={trip.key} className="rf-upcoming-row">
                <div className="rf-trip-time"><strong>{String(trip.first.horario || "").slice(0, 5)}</strong><span>{weekdayShort(dateKey)}</span></div>
                <div className="rf-trip-info">
                  <strong>{trip.first.trecho === "ida" ? "Ida" : "Volta"} · {trip.first.empresa_destino_nome || getDestinoLabel(config)}</strong>
                  <span>{trip.pedidos} {trip.pedidos === 1 ? "pedido" : "pedidos"}, {trip.passageiros} {trip.passageiros === 1 ? "passageiro" : "passageiros"}</span>
                </div>
                <span className={`rf-upcoming-state${trip.built ? " is-built" : ""}`}>{trip.built ? "Em montagem" : "Não montada"}</span>
                <Btn size="sm" variant={trip.built ? "secondary" : "primary"} onClick={() => openPedidos?.({ tab: trip.built ? "planejamento" : "a_fazer", focusDate: dateKey })}>
                  {trip.built ? "Abrir montagem" : "Montar vans"}
                </Btn>
              </li>;
            })}
          </ul>
        )}
      </Card>
      <h2 className="rf-resource-title">Cadastros da operação</h2>
      <div className="rf-resources">
        {stats.map(s => (
          <button key={s.label} type="button" aria-label={`Ver ${s.label.toLowerCase()} cadastrados`} onClick={() => setPage(s.action)}>
            <div className="rf-resource-value">{s.value}</div>
            <div className="rf-resource-label">{s.label}</div>
            <span className="rf-resource-action">Ver {s.label.toLowerCase()} <Icon name="arrow-right" size={18} /></span>
          </button>
        ))}
      </div>
    </div>
  );
}
